/* eslint-disable react-refresh/only-export-components -- the toast provider component and its useToast hook are intentionally colocated. */
import { createContext } from 'preact'
import { useContext, useState, useCallback, useMemo } from 'preact/hooks'
import type { ToastMessage } from '../components/notifications/ToastContainer'
import { ACTION_NOT_PERMITTED_MESSAGE } from '@/utils/permissionMessages'

interface ValidationError {
  Title: string
  Message: string
}

/** Subset of ApiResponse needed by showErrorUnless422 — keeps this hook decoupled from api/client. */
interface ApiErrorish {
  status: number
  error?: string
}

interface ToastContextType {
  toasts: ToastMessage[]
  addToast: (toast: Omit<ToastMessage, 'id'>) => void
  removeToast: (id: string) => void
  showSuccess: (title: string, message: string, duration?: number) => void
  showError: (title: string, message: string, duration?: number) => void
  /** Like showError, but suppresses the toast for 422 responses since the global
   *  validation-error handler renders those itself. */
  showErrorUnless422: (title: string, response: ApiErrorish, fallback?: string) => void
  showWarning: (title: string, message: string, duration?: number) => void
  showInfo: (title: string, message: string, duration?: number) => void
  showValidationErrors: (errors: ValidationError[], duration?: number) => void
  clearAll: () => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export function useToast() {
  const context = useContext(ToastContext)
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

interface ToastProviderProps {
  children: preact.ComponentChildren
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastMessage[]>([])

  const generateId = () => Math.random().toString(36).substring(2) + Date.now().toString(36)

  const addToast = useCallback((toast: Omit<ToastMessage, 'id'>) => {
    const id = generateId()
    const newToast: ToastMessage = { ...toast, id }
    setToasts(prev => [...prev, newToast])
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id))
  }, [])

  const showSuccess = useCallback((title: string, message: string, duration?: number) => {
    addToast({ type: 'success', title, message, duration })
  }, [addToast])

  const showError = useCallback((title: string, message: string, duration?: number) => {
    addToast({ type: 'error', title, message, duration: duration || 7000 }) // Errors stay longer
  }, [addToast])

  const showErrorUnless422 = useCallback((title: string, response: ApiErrorish, fallback?: string) => {
    if (response.status === 422) return
    // The client never surfaces a 403 body — `processResponse` returns a fixed string before reading
    // one — and the middleware's own text is a capability name that means nothing to a client
    // reviewer anyway. This is the one message that says why the button did not work, so it carries
    // the caller's title; the global handler stays quiet unless the roles really moved (ORB-698).
    if (response.status === 403) {
      showError(title, ACTION_NOT_PERMITTED_MESSAGE)
      return
    }
    showError(title, response.error || fallback || 'Something went wrong.')
  }, [showError])

  const showWarning = useCallback((title: string, message: string, duration?: number) => {
    addToast({ type: 'warning', title, message, duration })
  }, [addToast])

  const showInfo = useCallback((title: string, message: string, duration?: number) => {
    addToast({ type: 'info', title, message, duration })
  }, [addToast])

  const showValidationErrors = useCallback((errors: ValidationError[], duration?: number) => {
    errors.forEach(error => {
      showError(error.Title, error.Message, duration)
    })
  }, [showError])

  const clearAll = useCallback(() => {
    setToasts([])
  }, [])

  const value: ToastContextType = useMemo(() => ({
    toasts,
    addToast,
    removeToast,
    showSuccess,
    showError,
    showErrorUnless422,
    showWarning,
    showInfo,
    showValidationErrors,
    clearAll
  }), [toasts, addToast, removeToast, showSuccess, showError, showErrorUnless422, showWarning, showInfo, showValidationErrors, clearAll])

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  )
}
